<template>
  <b-container class="square-terminal-device-codes-list">
    <b-row v-if="testMode">
      <b-col>
        <div
          class="square-sandbox-mode-notice"
          data-cy="square-sandbox-mode-notice"
        >
          <font-awesome-icon
            icon="triangle-exclamation"
            class="notice-icon"
            size="lg"
          />
          <div class="notice-content">
            <strong>Sandbox mode active.</strong>
            Locations and devices added here will not appear in the list &mdash; sandbox mode uses a predefined hard-coded set of locations and devices to enable payment processing.
          </div>
        </div>
      </b-col>
    </b-row>

    <loading-spinner
      v-if="loading"
      data-cy="devices-codes-loading-spinner"
    >
      <div slot="title">
        Please wait loading Square terminal device codes
      </div>
    </loading-spinner>

    <!-- Status Messages - Always visible when message exists and not loading -->
    <b-row v-if="message && !loading">
      <b-col>
        <div
          class="message"
          :class="message.type"
          @click="dismissMessages"
        >
          <div class="inner">
            <font-awesome-icon
              v-if="!errorMessage"
              icon="check-circle"
              size="2x"
              class="icon"
              fixed-width
              color="green"
            />
            <font-awesome-icon
              v-if="errorMessage"
              icon="triangle-exclamation"
              size="2x"
              class="icon"
              fixed-width
              color="red"
            />
            <div>{{ message.text }}</div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Empty State - Only when no devices AND no message AND not showing create form -->
    <b-row v-if="noDevicesCodes && !loading && !message && !showCreateDeviceCodeForm">
      <b-col>
        <div class="no-devices-codes">
          <div class="icon-container">
            <font-awesome-icon
              icon="hard-drive"
              size="3x"
              class="icon"
              fixed-width
            />
          </div>
          <div class="message">
            There are no terminal device codes configured. Please add one.
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Empty State when message exists AND not showing create form -->
    <b-row v-if="noDevicesCodes && !loading && message && !showCreateDeviceCodeForm">
      <b-col>
        <div class="no-devices-codes">
          <div class="icon-container">
            <font-awesome-icon
              icon="hard-drive"
              size="3x"
              class="icon"
              fixed-width
            />
          </div>
          <div class="message">
            There are no terminal device codes configured. Please add one.
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Device Codes List - Hidden when create form is active -->
    <b-row v-if="!noDevicesCodes && !loading && !showCreateDeviceCodeForm">
      <b-col>
        <div class="device-list-wrapper">
          <div
            v-for="(deviceCode, idx) in deviceCodes"
            :key="idx"
            class="device"
          >
            <square-terminal-device-code-details
              :device="deviceCode"
              :remove="true"
              @removed="deviceCodeRemoved"
              @error="deviceCodeRemoveError"
            />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Add Device Button - Show when not loading and not showing create form -->
    <b-row v-if="!loading && !showCreateDeviceCodeForm">
      <b-col>
        <div class="add-terminal-device">
          <b-button
            variant="primary"
            @click="showCreateForm"
          >
            Add terminal device code
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Create Device Code Form - Show when form is active -->
    <b-row v-if="!loading && showCreateDeviceCodeForm">
      <b-col>
        <div class="create-device-form">
          <div class="form-header">
            <h4 class="form-title">
              Create Terminal Device Code
            </h4>
          </div>

          <div class="form-description">
            <div class="info-section">
              <div class="info-content">
                <h5>
                  <font-awesome-icon
                    icon="info-circle"
                    class="info-icon"
                    size="lg"
                  />
                  About Device Codes
                </h5>
                <p>
                  Device codes are used to pair Square Terminal devices with your Square account.
                  Once you create a device code, you'll receive a pairing code that needs to be
                  entered on your Square Terminal device.
                </p>
              </div>
            </div>

            <div class="warning-section">
              <font-awesome-icon
                icon="triangle-exclamation"
                class="warning-icon"
                size="3x"
              />
              <div class="warning-content">
                <strong>Important:</strong> Device codes that remain unpaired will automatically
                expire and be removed after 5 minutes. Make sure your Square Terminal device
                is ready before creating the code.
              </div>
            </div>
          </div>

          <SquareTerminalCreateDeviceCode
            @success="newDeviceCodeAdded"
            @error="newDeviceCodeError"
            @cancel="hideCreateForm"
          />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import SquareTerminalDeviceCodeDetails from './SquareTerminalDeviceCodeDetails.vue'
import SquareTerminalCreateDeviceCode from './SquareTerminalCreateDeviceCode.vue'

export default {
  name: 'square-terminal-device-codes-list',
  components: {
    SquareTerminalDeviceCodeDetails,
    SquareTerminalCreateDeviceCode
  },
  props: {
    allowSimulatedReader: {
      type: Boolean,
      required: false,
      defaultValue: false
    }
  },
  data: function() {
    return {
      loading: false,
      showCreateDeviceCodeForm: false,
      message: null
    }
  },
  computed: {
    noDevicesCodes: function() {
      return this.deviceCodes.length === 0
    },
    deviceCodes: function() {
      return this.$store.state.square.terminalDeviceCodes
    },
    errorMessage: function() {
      return (this.message && this.message.type === 'error')
    },
    testMode: function() {
      return this.$store.getters['square/testMode']
    }
  },
  watch: {
    loading: function(newVal) {
      this.$emit('loading', newVal)
    }
  },
  mounted: function() {
    this.loading = true
    return Promise.all([
      this.$store.dispatch('square/loadDeviceCodesList', { force: true }),
      this.$store.dispatch('square/loadLocations', { force: true })
    ])
      .catch(err => {
        const errorText = err.response?.data?.message || err.message || 'An unexpected error occurred'
        this.displayMessage(`Failed to load device codes: ${errorText}`, 'error')
      })
      .finally(_ => {
        this.loading = false
      })
  },
  methods: {
    displayMessage: function(message, type, timeout) {
      this.message = {
        type: (type === undefined) ? 'success' : type,
        text: message
      }
      setTimeout(() => {
        this.message = null
      }, timeout === undefined ? 15000 : timeout)
    },
    dismissMessages: function(type) {
      this.message = null
    },
    newDeviceCodeAdded: function(device) {
      this.showCreateDeviceCodeForm = false
      this.displayMessage(`New device code ${device.name || device.label} was successfully created.`, 'success', 2500)
    },
    newDeviceCodeError: function({ device, error }) {
      const errorText = error.response?.data?.message || error.message || 'An unexpected error occurred'
      this.displayMessage(`Error creating device code ${device.name || device.label} -- ${errorText}`, 'error')
    },
    deviceCodeRemoved: function(device) {
      console.log('deviceCodeRemoved called with device:', device)
      this.displayMessage(`Device ${device.id} - ${device.name || device.label} was removed successfully.`)
    },
    deviceCodeRemoveError: function({ device, error }) {
      const errorText = error.response?.data?.message || error.message || 'An unexpected error occurred'
      this.displayMessage(
        `Device  ${device.id} - ${device.name || device.label} could not be removed.<br>Reason: ${errorText}`,
        'error'
      )
    },
    showCreateForm: function() {
      this.showCreateDeviceCodeForm = true
    },
    hideCreateForm: function() {
      this.showCreateDeviceCodeForm = false
    }
  }
}
</script>

<style lang="scss" scoped>
  .square-sandbox-mode-notice {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    background-color: #fff3cd;
    border: 1px solid #ffeeba;
    border-left: 4px solid #ff9800;
    border-radius: 4px;
    color: #663c00;
    line-height: 1.4;

    .notice-icon {
      color: #ff9800;
      margin-right: 0.6rem;
      flex-shrink: 0;
      margin-top: 0.15rem;
    }

    .notice-content {
      flex: 1;

      strong {
        margin-right: 0.25rem;
      }
    }
  }

  .message {
    padding: 1.2rem 0.8rem;
    &.error {
      .inner {
        background-color: lightpink;
        color: darkred;
      }
    }

    &.success {
      .inner {
        background-color: lightgreen;
        color: darkgreen;
      }
    }

    .inner {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 1.2rem 0.5rem;
      div {
        margin-left: 2.2rem;
      }
    }
  }

  .square-terminal-device-codes-list {
    .no-devices {
      display: flex;
      flex-direction: row;
      justify-content: center;
      padding: 2.2rem;
      align-items: center;
    }

    .no-devices-codes {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      padding: 3rem 2rem;
      color: #6c757d;

      .icon-container {
        margin-bottom: 1.5rem;

        .icon {
          opacity: 0.6;
        }
      }

      .message {
        font-size: 1.1rem;
        line-height: 1.5;
        max-width: 400px;
      }
    }

    .device-list-wrapper {
      padding: 0;
      list-style: none;
      display: grid;
      grid-auto-columns: 1fr;
      grid-auto-rows: 1fr;
      grid-template-columns: repeat(2, 1fr);
      gap: .5rem;
      margin-bottom: 2rem;
    }

    .add-terminal-device {
      margin-top: 3rem;
    }

    .create-device-form {
      margin-top: 2rem;
      padding: 2rem;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      background-color: #f8f9fa;

      .form-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 2rem;
        padding-bottom: 1rem;
        border-bottom: 1px solid #dee2e6;

        .form-title {
          margin: 0;
          font-size: 1.25rem;
          color: #333;
          flex: 1;
        }

        .cancel-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.375rem 0.75rem;
          border-radius: 0.375rem;
          transition: all 0.2s ease;

          &:hover {
            background-color: #6c757d;
            border-color: #6c757d;
            color: #ffffff;
          }
        }
      }

      .form-description {
        margin-bottom: 2rem;

        .info-section {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 1rem;
          background-color: #e7f3ff;
          border: 1px solid #b8daff;
          border-radius: 6px;
          margin-bottom: 1rem;

          .info-content {
            flex: 1;

            h5 {
              display: flex;
              align-items: center;
              margin: 0 0 0.5rem 0;
              font-size: 1.1rem;
              color: #0066cc;
              font-weight: 600;

              .info-icon {
                color: #007bff;
                margin-right: 0.5rem;
              }
            }

            p {
              margin: 0;
              line-height: 1.5;
              color: #004085;
            }
          }
        }

        .warning-section {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          padding: 1rem;
          background-color: #fff3cd;
          border: 1px solid #ffeaa7;
          border-radius: 6px;

          .warning-icon {
            color: #ffc107;
            margin-right: 1rem;
            margin-top: 0;
            flex-shrink: 0;
            align-self: flex-start;
          }

          .warning-content {
            flex: 1;
            color: #856404;
            line-height: 1.5;
            margin-top: 0.5rem;

            strong {
              font-weight: 600;
            }
          }
        }
      }
    }
  }
</style>
